import axios from "axios";
import API_URLS from "../../../config/global_variables";

const authToken = () => {
  try {
    return JSON.parse(localStorage.userData || "{}").token || "";
  } catch {
    return "";
  }
};

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: authToken(),
});

const ERROR_MESSAGES = {
  VALIDATION_ERROR: "Invalid filter — please check your inputs.",
  BC_API_ERROR: "BigCommerce request failed. Check store credentials.",
  NOT_FOUND: "Blog not found.",
  SYNC_IN_PROGRESS: "A sync is already running for this store.",
  BC_RATE_LIMITED: "BigCommerce is rate-limiting. Retry shortly.",
  SERVER_ERROR: "Something went wrong. Please try again.",
};

const mapError = (err) => {
  const apiErr = err?.response?.data?.error;
  const code = apiErr?.code;
  const status = err?.response?.status;
  const fallback =
    status === 502
      ? "BigCommerce unavailable, try again later."
      : ERROR_MESSAGES.SERVER_ERROR;
  return {
    code: code || "SERVER_ERROR",
    status,
    message: ERROR_MESSAGES[code] || apiErr?.message || fallback,
    details: apiErr?.details || [],
  };
};

const fetchBlogs = async (filters) => {
  const params = { store_label: filters.store_label };
  if (filters.search)   params.search  = filters.search;
  if (filters.keyword)  params.keyword = filters.keyword;
  if (filters.tag_filter && filters.tag_filter !== "all")
    params.tag_filter = filters.tag_filter;
  if (filters.is_published !== undefined && filters.is_published !== null && filters.is_published !== "")
    params.is_published = filters.is_published;
  if (filters.is_default !== undefined && filters.is_default !== null && filters.is_default !== "")
    params.is_default = filters.is_default;
  params.page  = filters.page  || 1;
  params.limit = filters.limit || 20;

  const res = await axios.get(API_URLS.GET_BLOGS, { headers: headers(), params });
  const payload = res?.data?.data || {};
  return {
    items: payload.items || [],
    pagination: payload.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 },
  };
};

const fetchBlogById = async (id) => {
  const res = await axios.get(`${API_URLS.GET_BLOG_BY_ID}/${id}`, { headers: headers() });
  return res?.data?.data || null;
};

const deleteBlog = async (id) => {
  const res = await axios.delete(`${API_URLS.DELETE_BLOG}/${id}`, { headers: headers() });
  return res?.data?.data || null;
};

const triggerSync = async (storeLabel) => {
  const res = await axios.post(
    API_URLS.SYNC_BLOGS,
    { store: storeLabel, mode: "async" },
    { headers: headers() }
  );
  return res?.data?.data || null;
};

const fetchSyncStatus = async (storeHash) => {
  const res = await axios.get(API_URLS.SYNC_BLOGS_STATUS, {
    headers: headers(),
    params: { store_hash: storeHash },
  });
  return res?.data?.data || { running: false };
};

// Set/unset a blog as the store default. Server enforces single-default-per-store atomically.
const setBlogDefault = async (id, isDefault) => {
  const blogId = Number(id);
  if (!Number.isInteger(blogId) || blogId <= 0) throw new Error("Invalid blog id");
  const is_default = Number(isDefault) === 1 ? 1 : 0;
  const res = await axios.put(
    `${API_URLS.GET_BLOG_BY_ID}/${encodeURIComponent(String(blogId))}/default`,
    { is_default },
    { headers: headers() }
  );
  return res?.data?.data || null;
};

const apiHelpers = {
  fetchBlogs,
  fetchBlogById,
  deleteBlog,
  triggerSync,
  fetchSyncStatus,
  setBlogDefault,
  mapError,
};

export default apiHelpers;
